// Backend #507 (G2): the order-placement gate rejects a checkout whose cart
// positions are no longer orderable with 422 + per-position reasons. The BFF
// forwards Sales.Api's body verbatim: { positions: [{ skuId, reason, ... }] }.
// The same reason vocabulary is used by the cart read's
// CartPosition.unavailableReason ('notFound' | 'inactive' | 'imported').
export type OrderValidationReason =
  | 'inactive'
  | 'notFound'
  | 'insufficientStock'
  // Backend #525: the listing was re-currencied while carted — remove + re-add adopts the
  // new currency explicitly (drift is never silently accepted, mixing never converted).
  | 'currencyMismatch'
  // Backend #562 (decision 2): the listing was imported from an external shop by a scan
  // provider. Unlike every other reason this one is PERMANENT — an imported listing never
  // becomes orderable here, so removing the line is the only resolution.
  | 'imported'
  // Backend #579 (slice 1): the listing's VAT rate is still undetermined (the seller has not
  // set one). Reversible — the line becomes orderable again once the seller picks a rate.
  | 'vatUndetermined';

export interface OrderPositionValidationError {
  skuId: string;
  reason: OrderValidationReason;
  // Only set for reason 'insufficientStock'.
  requestedQuantity?: number;
  availableQuantity?: number;
  // Only set for reason 'currencyMismatch' (#525): the position's add-time snapshot vs. the
  // listing's live currency.
  cartCurrency?: string;
  skuCurrency?: string;
}

export interface OrderValidationProblem {
  positions: OrderPositionValidationError[];
}

const REASONS: ReadonlyArray<string> = [
  'inactive',
  'notFound',
  'insufficientStock',
  'currencyMismatch',
  'imported',
  'vatUndetermined',
];

/**
 * True for a code out of this vocabulary (#178). The add-to-cart 422 carries one of these as an
 * `ApiError.code` since backend #682, so the coded path has to tell them apart from the general
 * error codes — they are translated here, per position and with quantities, not in the code
 * dictionary.
 */
export function isOrderValidationReason(code: string): code is OrderValidationReason {
  return REASONS.includes(code);
}

export function orderValidationReasonText(error: {
  reason: OrderValidationReason;
  requestedQuantity?: number;
  availableQuantity?: number;
  cartCurrency?: string;
  skuCurrency?: string;
}): string {
  switch (error.reason) {
    case 'notFound':
      return $localize`Dieser Artikel ist nicht mehr verfügbar. Bitte entfernen Sie ihn aus dem Warenkorb.`;
    case 'inactive':
      return $localize`Dieser Artikel ist derzeit nicht bestellbar (Angebot deaktiviert).`;
    case 'insufficientStock':
      return $localize`Nicht genügend Bestand: angefragt ${error.requestedQuantity}, verfügbar ${error.availableQuantity}.`;
    case 'currencyMismatch':
      return $localize`Die Währung dieses Artikels hat sich geändert (${error.skuCurrency} statt ${error.cartCurrency}). Bitte entfernen Sie ihn und legen Sie ihn erneut in den Warenkorb.`;
    case 'imported':
      return $localize`Dieser Artikel wird von einem externen Shop angeboten und kann hier nicht bestellt werden. Bitte entfernen Sie ihn aus dem Warenkorb.`;
    case 'vatUndetermined':
      return $localize`Für diesen Artikel hat der Anbieter noch keinen Mehrwertsteuersatz festgelegt. Er ist derzeit nicht bestellbar – bitte versuchen Sie es später erneut oder entfernen Sie ihn aus dem Warenkorb.`;
    // A reason this build does not know yet (the backend vocabulary grows independently)
    // must not render as a blank warning — degrade to the pessimistic generic text rather
    // than returning undefined.
    default:
      return $localize`Dieser Artikel ist derzeit nicht bestellbar. Bitte entfernen Sie ihn aus dem Warenkorb.`;
  }
}
